<template>
  <div class="homepage">
    <!-- Hero Section -->
    <section class="hero">
      <div class="hero-wrap">
        <div class="hero-left">
          <!-- State-based welcome message -->
          <div v-if="userState === 'new'" data-state="new">
            <h1>Welcome, {{ userName }}</h1>
            <p class="lead">Our Search Assistant will help you find a clinical trial that is right for you.</p>
          </div>
          <div v-if="userState === 'back'" data-state="back">
            <h1>Welcome back, {{ userName }}</h1>
            <p class="lead">Your search is saved. Pick up where you left off, or start a new one.</p>
          </div>

          <!-- CTA Actions -->
          <div class="actions">
            <button 
              class="btn btn-primary" 
              type="button" 
              @click="startSearch"
              id="hero-cta"
              aria-label="Start my search"
            >
              <span class="cta-label">{{ userState === 'back' ? 'Continue my search' : 'Start my search' }}</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M5 12h14M13 6l6 6-6 6"/>
              </svg>
            </button>
            <button 
              v-if="userState === 'back'" 
              class="btn btn-quiet" 
              type="button" 
              @click="startNewSearch"
              data-state="back"
            >
              Start a new search
            </button>
          </div>

          <!-- Reassurance Message -->
          <p class="reassure">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z"/>
              <path d="M17 21v-8H7v8M7 3v5h8"/>
            </svg>
            <span>Your conversation saves as you go, so you can take a break and come back anytime.</span>
          </p>
        </div>

        <!-- Focus Card -->
        <aside class="focus" aria-labelledby="focus-title">
          <span class="eyebrow">Now screening</span>
          <p class="focus-title" id="focus-title">
            <span class="live" aria-hidden="true"></span>
            Dementia studies
          </p>
          <p>Our list of trials is steadily growing. Right now the Search Assistant is actively screening for trials in dementia.</p>
          <p class="focus-foot">
            Looking for a different kind of study? 
            <a href="#notify" @click.prevent="scrollToNotify">Get notified when new trials open</a>
          </p>
        </aside>
      </div>
    </section>

    <!-- How It Works Section -->
    <section class="block band" id="how" aria-labelledby="how-title">
      <div class="wrap">
        <div class="section-head">
          <h2 id="how-title">How it works</h2>
          <p>Three steps. The assistant guides you through each one.</p>
        </div>
        <ol class="steps">
          <li>
            <span class="step-num" aria-hidden="true"></span>
            <h3>Tell the assistant what you're looking for</h3>
            <p>Name the condition or therapeutic area, for example Alzheimer's disease, or any condition you are seeing a doctor about.</p>
          </li>
          <li>
            <span class="step-num" aria-hidden="true"></span>
            <h3>Answer questions about your health</h3>
            <p>
              These check the study's <span class="term">inclusion criteria</span> 
              (what needs to be true to take part) and <span class="term">exclusion criteria</span> 
              (what may prevent you from taking part safely).
            </p>
            <p class="aside">It's fine not to know an answer.</p>
          </li>
          <li>
            <span class="step-num" aria-hidden="true"></span>
            <h3>Find out if you're a good fit</h3>
            <p>If you are, we ask your permission before sending your information to the study team. They then contact you directly.</p>
          </li>
        </ol>
      </div>
    </section>

    <!-- Tips & Privacy Section -->
    <section class="block" aria-label="Tips and privacy">
      <div class="wrap pair">
        <!-- Things you can say panel -->
        <div class="panel">
          <h2>Things you can say anytime</h2>
          <p>Type any of these into the chat.</p>
          <ul class="phrases">
            <li class="bubble">
              "I do not know."
              <span class="bubble-when">When you aren't sure of an answer</span>
            </li>
            <li class="bubble">
              "I would like to confirm with my doctor."
              <span class="bubble-when">When you want to check first</span>
            </li>
            <li class="bubble">
              "Show me the answers I've given."
              <span class="bubble-when">To review and add anything you missed</span>
            </li>
            <li class="bubble">
              "Can you show me where we left off?"
              <span class="bubble-when">When you come back after a break</span>
            </li>
          </ul>
        </div>

        <!-- Your information, your choice panel -->
        <div class="panel">
          <h2>Your information, your choice</h2>
          <p>What happens with your answers.</p>
          <ul class="checks">
            <li>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z"/>
                <path d="m9 12 2 2 4-4"/>
              </svg>
              <span><strong>Nothing is shared without your permission.</strong> We ask before sending anything to a study team.</span>
            </li>
            <li>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="9"/>
                <path d="M12 7v5l3 2"/>
              </svg>
              <span><strong>Your conversation is saved.</strong> Return to it whenever you're ready.</span>
            </li>
            <li>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M12 20h9"/>
                <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/>
              </svg>
              <span><strong>You stay in control of your answers.</strong> Review them and add anything you missed at any time.</span>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <!-- Notification Section -->
    <section id="notify" aria-labelledby="notify-title">
      <div class="wrap">
        <div class="notify">
          <div class="notify-intro">
            <h2 id="notify-title">Looking for a different kind of study?</h2>
            <p>Tell us what you're searching for and we'll be in touch as new trials become available.</p>
            <p class="fine">We'll contact you at the email on your account.</p>
          </div>
          <div>
            <form 
              id="notify-form" 
              @submit.prevent="handleNotifySubmit"
              novalidate
              v-if="!notifySuccess"
            >
              <label for="study-interest">What kind of studies are you looking for?</label>
              <p class="hint" id="study-hint">For example: a condition, your location, or an age group.</p>
              <textarea 
                id="study-interest" 
                name="study-interest" 
                v-model="studyInterest" 
                aria-describedby="study-hint study-error"
                :aria-invalid="notifyError ? 'true' : 'false'"
              ></textarea>
              <p class="error" id="study-error" v-if="notifyError">
                Tell us what kind of study you're looking for, then press Notify me.
              </p>
              <button class="btn btn-submit" type="submit" :disabled="notifying">
                {{ notifying ? 'Notifying...' : 'Notify me' }}
              </button>
            </form>
            
            <!-- Success Message -->
            <div 
              class="success" 
              id="notify-done" 
              role="status" 
              v-if="notifySuccess"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="9"/>
                <path d="m8 12 3 3 5-6"/>
              </svg>
              <span>
                <strong>Thanks, we've noted your interest.</strong> 
                We'll email you when a matching trial becomes available.
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Login Modal -->
    <LoginModal
      v-if="showLogin"
      :prefill-email="prefilledEmail"
      :info-message="loginInfoMessage"
      @close="handleCloseLogin"
      @login-success="handleLoginSuccess"
      @show-register="handleShowRegister"
      @show-forgot-password="handleShowForgotPassword"
    />

    <!-- Registration Modal -->
    <RegisterModal
      v-if="showRegister"
      @close="showRegister = false"
      @register-success="handleRegisterSuccess"
      @show-login="handleShowLogin"
    />

    <!-- Forgot Password Modal -->
    <ForgotPasswordModal
      v-if="showForgotPassword"
      @close="showForgotPassword = false"
      @show-login="handleShowLogin"
    />

    <!-- Mobile Sticky CTA -->
    <div 
      class="sticky-cta" 
      id="sticky" 
      :class="{ show: stickyCtaVisible }" 
      :aria-hidden="!stickyCtaVisible"
    >
      <button 
        class="btn btn-primary" 
        type="button" 
        @click="startSearch"
        :tabindex="stickyCtaVisible ? 0 : -1"
      >
        <span class="cta-label">{{ userState === 'back' ? 'Continue my search' : 'Start my search' }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { ROUTES } from '@/constants'
import LoginModal from '@/components/modals/LoginModal.vue'
import RegisterModal from '@/components/modals/RegisterModal.vue'
import ForgotPasswordModal from '@/components/modals/ForgotPasswordModal.vue'
import { submitPatientStudyInterest } from '@/api/patient_notifications'
import { useSeo } from '@/composables/useSeo'

export default {
  name: 'HomePage',
  components: {
    LoginModal,
    RegisterModal,
    ForgotPasswordModal
  },
  setup() {
    useSeo({
      title: 'AI-powered clinical trial matching for patients',
      description: 'Discover clinical trials, check if you may qualify, and connect with research teams — through a free, secure AI search assistant.',
      type: 'website',
      canonical: '/'
    })

    const store = useStore()
    const router = useRouter()
    
    // State management
    const userState = ref('new')
    const studyInterest = ref('')
    const notifySuccess = ref(false)
    const notifyError = ref(false)
    const notifying = ref(false)
    const stickyCtaVisible = ref(false)
    
    // Hero and notify section visibility refs
    const heroCtaVisible = ref(true)
    const notifyVisible = ref(false)
    
    // Observer instances
    let heroObserver = null
    let notifyObserver = null
    
    const isAuthenticated = computed(() => store.getters['auth/isAuthenticated'])
    const currentUser = computed(() => store.getters['auth/currentUser'])
    const userName = computed(() => {
      return currentUser.value?.firstName || currentUser.value?.email?.split('@')[0] || 'there'
    })

    // Modal state
    const showLogin = ref(false)
    const showRegister = ref(false)
    const showForgotPassword = ref(false)
    const prefilledEmail = ref('')
    const loginInfoMessage = ref('')

    // Methods
    const setUserState = (state) => {
      userState.value = state
    }

    const startSearch = () => {
      if (isAuthenticated.value) {
        router.push(ROUTES.CHAT)
      } else {
        showLogin.value = true
      }
    }

    const startNewSearch = () => {
      setUserState('new')
      router.push(ROUTES.WELCOME)
    }

    const scrollToNotify = () => {
      const notifySection = document.getElementById('notify')
      if (notifySection) {
        notifySection.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }

    const handleNotifySubmit = async () => {
      if (!studyInterest.value.trim()) {
        notifyError.value = true
        return
      }
      
      notifyError.value = false
      notifying.value = true
      
      try {
        await submitPatientStudyInterest(studyInterest.value)
        notifySuccess.value = true
        studyInterest.value = ''
      } catch (error) {
        console.error('Failed to submit study interest:', error)
        notifyError.value = true
      } finally {
        notifying.value = false
      }
    }

    const handleLoginSuccess = () => {
      showLogin.value = false
      prefilledEmail.value = ''
      loginInfoMessage.value = ''
      setUserState('back')
      router.push(ROUTES.WELCOME)
    }

    const handleRegisterSuccess = (payload) => {
      showRegister.value = false
      prefilledEmail.value = payload?.email || ''
      loginInfoMessage.value = 'Account created. Please sign in to receive your verification code.'
      showLogin.value = true
    }

    const handleCloseLogin = () => {
      showLogin.value = false
      prefilledEmail.value = ''
      loginInfoMessage.value = ''
    }

    const handleShowRegister = () => {
      showLogin.value = false
      loginInfoMessage.value = ''
      showRegister.value = true
    }

    const handleShowLogin = () => {
      showRegister.value = false
      showForgotPassword.value = false
      showLogin.value = true
    }

    const handleShowForgotPassword = () => {
      showLogin.value = false
      showRegister.value = false
      showForgotPassword.value = true
    }

    // Intersection Observer for sticky CTA
    const updateStickyCta = () => {
      stickyCtaVisible.value = !heroCtaVisible.value && !notifyVisible.value
    }

    onMounted(() => {
      // Add has-sticky class to body for footer padding
      document.body.classList.add('has-sticky')
      
      // Set up IntersectionObservers
      if ('IntersectionObserver' in window) {
        const heroCtaEl = document.getElementById('hero-cta')
        const notifyEl = document.getElementById('notify')
        
        if (heroCtaEl) {
          heroObserver = new IntersectionObserver((entries) => {
            heroCtaVisible.value = entries[0].isIntersecting
            updateStickyCta()
          }, { threshold: 0.1 })
          heroObserver.observe(heroCtaEl)
        }
        
        if (notifyEl) {
          notifyObserver = new IntersectionObserver((entries) => {
            notifyVisible.value = entries[0].isIntersecting
            updateStickyCta()
          }, { threshold: 0.1 })
          notifyObserver.observe(notifyEl)
        }
      }
      
      // Set user state based on authentication
      if (isAuthenticated.value) {
        setUserState('back')
      }
    })

    onUnmounted(() => {
      // Clean up observers
      if (heroObserver) heroObserver.disconnect()
      if (notifyObserver) notifyObserver.disconnect()
      document.body.classList.remove('has-sticky')
    })

    return {
      userState,
      userName,
      studyInterest,
      notifySuccess,
      notifyError,
      notifying,
      stickyCtaVisible,
      showLogin,
      showRegister,
      showForgotPassword,
      prefilledEmail,
      loginInfoMessage,
      isAuthenticated,
      setUserState,
      startSearch,
      startNewSearch,
      scrollToNotify,
      handleNotifySubmit,
      handleLoginSuccess,
      handleRegisterSuccess,
      handleCloseLogin,
      handleShowRegister,
      handleShowLogin,
      handleShowForgotPassword,
      faqRoute: ROUTES.FAQ
    }
  }
}
</script>

<style scoped>
.homepage {
  background: var(--page);
  color: var(--ink);
  font-family: var(--font);
  font-size: 16px;
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
  min-height: 100vh;
}

/* Typography */
h1, h2, h3 {
  margin: 0;
  text-wrap: balance;
  color: var(--green);
  line-height: 1.2;
}

p {
  margin: 0;
}

a {
  color: var(--green);
}

a:hover {
  text-decoration: underline;
}

:focus-visible {
  outline: 3px solid var(--teal);
  outline-offset: 2px;
  border-radius: 6px;
}

.wrap {
  max-width: 1080px;
  margin: 0 auto;
  padding-inline: clamp(16px, 4vw, 32px);
}

* {
  box-sizing: border-box;
}

/* Hero Section */
.hero {
  padding-block: clamp(40px, 7vw, 72px) clamp(32px, 5vw, 56px);
}

.hero-wrap {
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(0, 1fr);
  gap: clamp(24px, 5vw, 56px);
  align-items: center;
  background: var(--teal-soft);
  border-radius: var(--r-card);
  padding: clamp(24px, 4vw, 40px);
  max-width: 1080px;
  margin: 0 auto;
}

.hero-left {
  min-width: 0;
}

.hero h1 {
  font-size: clamp(34px, 5.2vw, 50px);
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--green);
}

.lead {
  font-size: clamp(18px, 2vw, 20px);
  color: var(--ink-2);
  margin-top: 14px;
  max-width: 34ch;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 20px;
  margin-top: 28px;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font: inherit;
  font-weight: 600;
  border-radius: 999px;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s;
}

.btn-primary {
  background: var(--green);
  color: var(--on-green);
  border: 0;
  font-size: 17px;
  padding: 15px 28px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

.btn-primary:hover {
  background: var(--green-hover);
}

.btn-primary svg {
  width: 18px;
  height: 18px;
}

.btn-quiet {
  background: transparent;
  border: 0;
  color: var(--green);
  font-size: 16px;
  padding: 8px 4px;
  text-decoration: underline;
  text-underline-offset: 4px;
}

.btn-quiet:hover {
  opacity: 0.8;
}

.reassure {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-top: 18px;
  font-size: 15px;
  color: var(--muted);
  max-width: 44ch;
}

.reassure svg {
  flex: none;
  width: 18px;
  height: 18px;
  margin-top: 3px;
  color: var(--teal);
}

/* Focus Card */
.focus {
  background: var(--surface);
  border-radius: var(--r-card);
  box-shadow: var(--shadow);
  padding: 26px 26px 22px;
}

.eyebrow {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--teal);
}

.focus-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
  font-size: 24px;
  font-weight: 700;
  color: var(--green);
}

.live {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #2E8B57;
  box-shadow: 0 0 0 4px rgba(46, 139, 87, 0.18);
  flex: none;
}

.focus p {
  margin-top: 10px;
  color: var(--ink-2);
  font-size: 15.5px;
}

.focus-foot {
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid var(--line);
  font-size: 15px;
  color: var(--muted);
}

.focus-foot a {
  font-weight: 600;
}

/* Sections */
section.block {
  padding-block: clamp(36px, 5vw, 56px);
}

.band {
  background: var(--surface);
  border-block: 1px solid var(--line);
}

.section-head {
  max-width: 60ch;
}

.section-head h2 {
  font-size: clamp(24px, 3vw, 28px);
  font-weight: 700;
}

.section-head p {
  margin-top: 8px;
  color: var(--muted);
}

/* Steps */
.steps {
  list-style: none;
  margin: 28px 0 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: clamp(16px, 3vw, 32px);
  counter-reset: step;
}

.steps li {
  counter-increment: step;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

.step-num {
  display: flex;
  align-items: center;
  gap: 12px;
}

.step-num::before {
  content: counter(step);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--green);
  color: var(--on-green);
  display: grid;
  place-items: center;
  font-weight: 700;
  font-size: 16px;
  flex: none;
}

.step-num::after {
  content: "";
  flex: 1;
  height: 2px;
  background: var(--line);
}

.steps li:last-child .step-num::after {
  background: transparent;
}

.steps h3 {
  font-size: 18px;
  color: var(--ink-2);
}

.steps p {
  font-size: 15.5px;
  color: var(--ink-2);
}

.term {
  font-weight: 600;
  color: var(--green);
}

.steps .aside {
  font-size: 14.5px;
  color: var(--muted);
}

/* Pair Section (Tips & Privacy) */
.pair {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: clamp(16px, 3vw, 32px);
}

.panel {
  min-width: 0;
}

.panel h2 {
  font-size: 22px;
}

.panel > p {
  margin-top: 6px;
  color: var(--muted);
  font-size: 15.5px;
}

/* Chat Bubbles */
.phrases {
  list-style: none;
  padding: 0;
  margin: 18px 0 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
}

.bubble {
  background: var(--teal-soft);
  color: var(--green);
  border-radius: 16px 16px 16px 4px;
  padding: 10px 16px;
  font-size: 16px;
  font-weight: 500;
  max-width: 100%;
}

.bubble-when {
  display: block;
  font-size: 13px;
  font-weight: 400;
  color: var(--muted);
  margin-top: 2px;
}

/* Checks List */
.checks {
  list-style: none;
  padding: 0;
  margin: 18px 0 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.checks li {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  font-size: 16px;
  color: var(--ink-2);
}

.checks svg {
  flex: none;
  width: 22px;
  height: 22px;
  color: var(--green);
  margin-top: 1px;
}

.checks strong {
  color: var(--green);
}

/* Notification Section */
.notify {
  background: var(--surface);
  border-radius: var(--r-card);
  box-shadow: var(--shadow);
  padding: clamp(22px, 4vw, 36px);
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr);
  gap: clamp(20px, 4vw, 40px);
  align-items: start;
}

.notify h2 {
  font-size: clamp(22px, 2.6vw, 26px);
}

.notify-intro p {
  margin-top: 8px;
  color: var(--ink-2);
}

.notify-intro .fine {
  margin-top: 12px;
  font-size: 14px;
  color: var(--muted);
}

form {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

label {
  font-weight: 600;
  font-size: 15px;
  color: var(--ink-2);
}

.hint {
  font-size: 14px;
  color: var(--muted);
  margin-top: -4px;
}

textarea {
  font: inherit;
  color: var(--ink-2);
  background: var(--field);
  border: 1px solid var(--field-line);
  border-radius: var(--r-field);
  padding: 12px 14px;
  min-height: 96px;
  resize: vertical;
  width: 100%;
  transition: border-color 0.15s, box-shadow 0.15s;
}

textarea:focus {
  outline: none;
  border-color: var(--green);
  box-shadow: 0 0 0 3px rgba(14, 59, 46, 0.15);
}

.btn-submit {
  align-self: flex-start;
  background: var(--green);
  color: var(--on-green);
  border: 0;
  border-radius: var(--r-field);
  font-size: 16px;
  padding: 12px 24px;
  margin-top: 6px;
  cursor: pointer;
  transition: background 0.15s;
  font-family: inherit;
  font-weight: 600;
}

.btn-submit:hover:not(:disabled) {
  background: var(--green-hover);
}

.btn-submit:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.error {
  color: #9F1D1D;
  font-size: 14px;
  margin-top: -4px;
}

.success {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  background: var(--teal-soft);
  border-radius: 12px;
  padding: 16px;
  color: var(--green);
}

.success svg {
  flex: none;
  width: 22px;
  height: 22px;
}

/* Mobile Sticky CTA */
.sticky-cta {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  background: var(--nav);
  border-top: 1px solid var(--line);
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
  display: none;
}

.sticky-cta.show {
  display: block;
}

.sticky-cta .btn-primary {
  width: 100%;
}

/* Responsive Design */
@media (max-width: 860px) {
  .hero .wrap,
  .notify {
    grid-template-columns: 1fr;
  }
  
  .steps {
    grid-template-columns: 1fr;
    gap: 24px;
  }
  
  .step-num::after {
    display: none;
  }
  
  .steps li {
    display: grid;
    grid-template-columns: 36px minmax(0, 1fr);
    column-gap: 14px;
    row-gap: 6px;
  }
  
  .steps li > *:not(.step-num) {
    grid-column: 2;
  }
  
  .steps li .step-num {
    grid-row: 1 / span 3;
  }
  
  .pair {
    grid-template-columns: 1fr;
    gap: 36px;
  }
}

@media (max-width: 560px) {
  .sticky-cta.show {
    display: block;
  }
  
  body.has-sticky footer {
    padding-bottom: 76px;
  }
}

@media (prefers-reduced-motion: reduce) {
  * {
    transition: none !important;
  }
}
</style>
